import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { readNeoOffice } from './api';
import { downloadArchiveFile } from './archiveDownload';

type Row = Record<string, unknown> & { id: number };
type Listing = { records: Row[]; total: number; page: number; size: number };
type Detail = Listing & {
  customer?: Row;
  case?: Row;
  customerId?: number | null;
};
type Summary = {
  run: number | null;
  runs: { id: number; completedAt: string }[];
  reconciliation: {
    attachmentInventoryComplete?: boolean;
    quarantinedRows?: number;
  };
  hitVersions: { datasetVersion: string; isDefault: boolean }[];
};
type Target = { kind: 'customers' | 'cases'; id: number };
const root = 'v1/neo-office/cm1/';
const base =
  process.env.REACT_APP_API_BASE_URL ?? process.env.REACT_APP_BASE_URL ?? '';
const kinds = [
  'customers',
  'cases',
  'timeline',
  'notes',
  'hits',
  'documents',
  'issues',
];
const customerSections = [
  'profiles',
  'timeline',
  'parameters',
  'connections',
  'risk',
  'cases',
  'notes',
  'documents',
  'links',
];
const caseSections = ['timeline', 'metadata', 'hits', 'notes', 'documents'];
const fieldLabels: Record<string, string> = {
  timeline: 'Case history & comments',
  notes: 'Additional notes',
  customId: 'CM1 customer ID',
  cm1CaseId: 'CM1 case ID',
  dowJonesId: 'Dow Jones ID',
  extraColumn9Raw: 'Unmapped source column 9',
  valueText: 'Value',
  reportedArchived: 'Archived in source',
  mappingStatus: 'Column mapping',
  profileUpdatedAt: 'Profile updated in source',
};
const label = (text: string) =>
  fieldLabels[text] ??
  text
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/^./, (c) => c.toUpperCase());
const display = (value: unknown, fallback = '') =>
  typeof value === 'string'
    ? value
    : typeof value === 'number'
    ? value.toString()
    : fallback;

function Value({ value }: { value: unknown }) {
  if (value === null || value === undefined || value === '')
    return <>Unavailable</>;
  if (typeof value === 'boolean') return <>{value ? 'Yes' : 'No'}</>;
  if (typeof value === 'object')
    return <pre>{JSON.stringify(value, null, 2)}</pre>;
  return <>{display(value, 'Unavailable')}</>;
}

function ArchiveDownload({
  path,
  filename,
  children,
}: {
  path: string;
  filename: string;
  children: string;
}) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const download = async () => {
    setPending(true);
    setError('');
    try {
      await downloadArchiveFile(`${base}/api/bo/${root}${path}`, filename);
    } catch (error) {
      setError(
        error instanceof Error
          ? error.message
          : 'Unable to download the archive file. Please try again.'
      );
    } finally {
      setPending(false);
    }
  };
  return (
    <div>
      <button className="neo-link" disabled={pending} onClick={download}>
        {pending ? 'Preparing download…' : children}
      </button>
      {error && <p role="alert">{error}</p>}
    </div>
  );
}

function CaseHistoryEvent({ row }: { row: Row }) {
  const step = display(row.workflowStep);
  const steps: Record<string, string> = {
    Vilande: 'Postponed (Vilande)',
    Arkiv: 'Archived (Arkiv)',
    Handläggare: 'Caseworker review (Handläggare)',
    Skapad: 'Created (Skapad)',
  };
  const title =
    row.actionType === 'AwakenedCase'
      ? 'Awakened (AwakenedCase)'
      : (steps[step] ?? step) || 'Case handling event';
  const source =
    typeof row.source === 'object' && row.source !== null
      ? (row.source as Record<string, unknown>)
      : {};
  return (
    <div className="neo-cm1-event">
      <h3>{title}</h3>
      <p className="neo-cm1-event-date">
        Event date (source): <Value value={row.occurredAt} />
      </p>
      <dl className="neo-provider-fields">
        <div>
          <dt>CM1 customer ID</dt>
          <dd>
            <Value value={row.sourceCustomId} />
          </dd>
        </div>
        <div>
          <dt>Recorded by</dt>
          <dd>
            <Value value={row.sourceUsername} />
          </dd>
        </div>
        <div>
          <dt>Workflow step</dt>
          <dd>
            <Value value={row.workflowStep} />
          </dd>
        </div>
        <div>
          <dt>Recorded action / outcome</dt>
          <dd>
            <Value value={row.actionType} />
          </dd>
        </div>
        <div>
          <dt>Case type</dt>
          <dd>
            <Value value={row.caseType} />
          </dd>
        </div>
      </dl>
      <h4>Analyst / system comment</h4>
      <p className="neo-cm1-comment">
        <Value value={row.comment} />
      </p>
      <details>
        <summary>Source details</summary>
        <dl className="neo-provider-fields">
          <div>
            <dt>Source file</dt>
            <dd>
              <Value value={source.name} />
            </dd>
          </div>
          <div>
            <dt>Source row</dt>
            <dd>
              <Value value={source.row} />
            </dd>
          </div>
          <div>
            <dt>Case created in source</dt>
            <dd>
              <Value value={row.sourceCaseCreatedAt} />
            </dd>
          </div>
          <div>
            <dt>Case last modified in source</dt>
            <dd>
              <Value value={row.sourceCaseLastModifiedAt} />
            </dd>
          </div>
        </dl>
      </details>
    </div>
  );
}

function RecordCard({
  row,
  kind,
  run,
  open,
}: {
  row: Row;
  kind: string;
  run: string;
  open: (target: Target) => void;
}) {
  const sourceFileId =
    row.sourceFileId ??
    (typeof row.source === 'object' && row.source !== null
      ? (row.source as Record<string, unknown>).fileId
      : null);
  return (
    <article className="neo-cm1-record">
      {kind === 'customers' && (
        <button
          className="neo-link"
          onClick={() => open({ kind: 'customers', id: row.id })}
        >
          {display(row.name ?? row.customId, 'Customer')}
        </button>
      )}
      {kind === 'cases' && (
        <button
          className="neo-link"
          onClick={() => open({ kind: 'cases', id: row.id })}
        >
          CM1 case {display(row.cm1CaseId)}
        </button>
      )}
      {(kind === 'hits' || kind === 'timeline') &&
        typeof row.case === 'number' && (
          <button
            className="neo-link"
            onClick={() => open({ kind: 'cases', id: row.case as number })}
          >
            Open case {display(row.cm1CaseId)}
          </button>
        )}
      {kind === 'notes' &&
        (typeof row.case === 'number' || typeof row.customer === 'number') && (
          <button
            className="neo-link"
            onClick={() =>
              open(
                typeof row.case === 'number'
                  ? { kind: 'cases', id: row.case }
                  : { kind: 'customers', id: row.customer as number }
              )
            }
          >
            Open {typeof row.case === 'number' ? 'case' : 'customer'}
          </button>
        )}
      {row.mappingStatus === 'PROVISIONAL' && (
        <p className="neo-notice">
          Column mapping is provisional. The additional source field is
          preserved below.
        </p>
      )}
      {row.metadataAvailable === false && (
        <p>
          Case metadata and customer link unavailable. Screening observations
          are preserved.
        </p>
      )}
      {row.profileAvailable === false && (
        <p>
          Customer profile unavailable. Related historical records are
          preserved.
        </p>
      )}
      {row.multipleLatestProfiles === true && (
        <p>
          Multiple profiles share the latest source date. All observations are
          preserved in Profiles.
        </p>
      )}
      {kind === 'timeline' ? (
        <CaseHistoryEvent row={row} />
      ) : (
        <dl className="neo-provider-fields">
          {Object.entries(row)
            .filter(
              ([key]) =>
                ![
                  'id',
                  'case',
                  'customer',
                  'person',
                  'organization',
                  'sourceFileId',
                  'profileAvailable',
                  'metadataAvailable',
                  'multipleLatestProfiles',
                ].includes(key)
            )
            .map(([key, value]) => (
              <div key={key}>
                <dt>{label(key)}</dt>
                <dd>
                  <Value value={value} />
                </dd>
              </div>
            ))}
        </dl>
      )}
      {typeof sourceFileId === 'number' && (
        <ArchiveDownload
          path={`sources/${sourceFileId}/download?${new URLSearchParams(
            run ? { run } : {}
          )}`}
          filename={`cm1-source-${sourceFileId}`}
        >
          Download preserved source
        </ArchiveDownload>
      )}
      {kind === 'documents' && row.availability === 'AVAILABLE' && (
        <ArchiveDownload
          path={`documents/${row.id}/download?${new URLSearchParams(
            run ? { run } : {}
          )}`}
          filename={display(row.name, `cm1-document-${row.id}`)}
        >
          Download original document
        </ArchiveDownload>
      )}
    </article>
  );
}

export type InitialCustomer = { id: number; run: number; section: string };

export default function Cm1Archive({
  initialCustomer,
}: { initialCustomer?: InitialCustomer } = {}) {
  const [run, setRun] = useState(
    initialCustomer ? String(initialCustomer.run) : ''
  );
  const [kind, setKind] = useState('customers');
  const [text, setText] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [target, setTarget] = useState<Target | null>(
    initialCustomer ? { kind: 'customers', id: initialCustomer.id } : null
  );
  const [section, setSection] = useState(
    initialCustomer?.section ?? 'profiles'
  );
  const [version, setVersion] = useState('');
  const [flag, setFlag] = useState('');
  const [state, setState] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [parameterText, setParameterText] = useState('');
  const [parameterFilter, setParameterFilter] = useState('');
  const params = new URLSearchParams({
    kind,
    q: query,
    page: String(page),
    section,
    version,
    flag,
    state,
    date_from: dateFrom,
    date_to: dateTo,
    filter: parameterFilter,
  });
  if (run) params.set('run', run);
  const queryParams = params.toString();
  const summary = useQuery({
    queryKey: ['neo', 'cm1-summary', run],
    queryFn: () =>
      readNeoOffice<Summary>(`${root}summary${run ? `?run=${run}` : ''}`),
    retry: false,
  });
  const results = useQuery({
    queryKey: ['neo', 'cm1-search', queryParams],
    queryFn: () => readNeoOffice<Listing>(`${root}search?${queryParams}`),
    enabled: !target && !!summary.data?.run,
    retry: false,
  });
  const detail = useQuery({
    queryKey: ['neo', 'cm1-detail', target, queryParams],
    queryFn: () =>
      readNeoOffice<Detail>(
        `${root}${target?.kind}/${target?.id}?${queryParams}`
      ),
    enabled: !!target && !!summary.data?.run,
    retry: false,
  });
  const open = (next: Target) => {
    setTarget(next);
    setSection(next.kind === 'customers' ? 'profiles' : 'timeline');
    setVersion('');
    setFlag('');
    setPage(1);
    setDateFrom('');
    setDateTo('');
    setParameterText('');
    setParameterFilter('');
  };
  const active = target ? detail : results;
  const currentKind = target ? section : kind;
  const dateLabel: Record<string, string> = {
    customers: 'Profile updated',
    cases: 'Case created',
    profiles: 'Profile updated',
    metadata: 'Case created',
    timeline: 'Event date',
    hits: 'Hit date',
    notes: 'Note created',
    parameters: 'Parameter created',
    connections: 'Connection date',
    risk: 'Risk effective date',
  };
  const sections =
    target?.kind === 'customers' ? customerSections : caseSections;
  const primary = detail.data?.customer ?? detail.data?.case;
  const linkedCustomerId = detail.data?.customerId;
  return (
    <section aria-label="Historical CM1 archive">
      <h1>CM1 archive</h1>
      <p>
        Search historical customer profiles, cases, screening observations,
        comments and documents. Source dates are shown as reported by CM1; their
        timezone is unconfirmed.
      </p>
      {summary.isError && (
        <p role="alert">
          Unable to load the archive:{' '}
          {summary.error instanceof Error
            ? summary.error.message
            : 'Please try again.'}
        </p>
      )}
      {summary.isPending && <p role="status">Loading archive…</p>}
      {summary.data && !summary.data.run && (
        <p>No historical archive has been published yet.</p>
      )}
      {!!summary.data?.run && (
        <>
          <div className="neo-filters">
            <label>
              Archive import
              <select
                value={run}
                onChange={(e) => {
                  setRun(e.target.value);
                  setTarget(null);
                  setPage(1);
                }}
              >
                <option value="">Latest published import</option>
                {summary.data.runs.map((r) => (
                  <option key={r.id} value={r.id}>
                    Import {r.id} ·{' '}
                    {new Date(r.completedAt).toLocaleDateString('en-GB')}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {!summary.data.reconciliation.attachmentInventoryComplete && (
            <p className="neo-notice">
              Document inventory is incomplete. Unlisted documents remain
              pending, rather than being marked missing.
            </p>
          )}
          {!!summary.data.reconciliation.quarantinedRows && (
            <p className="neo-notice">
              {summary.data.reconciliation.quarantinedRows} source{' '}
              {summary.data.reconciliation.quarantinedRows === 1
                ? 'row needs'
                : 'rows need'}{' '}
              review. Original values are preserved under Import issues.
            </p>
          )}
          {target ? (
            <>
              <button
                className="neo-back"
                onClick={() => {
                  setTarget(null);
                  setPage(1);
                }}
              >
                ← Back to archive search
              </button>
              {primary && (
                <h2>
                  {target.kind === 'customers'
                    ? display(primary.name ?? primary.customId)
                    : `CM1 case ${display(primary.cm1CaseId)}`}
                </h2>
              )}
              {primary && target.kind === 'cases' && (
                <dl className="neo-provider-fields">
                  <div>
                    <dt>CM1 customer ID</dt>
                    <dd>
                      <Value value={primary.customId} />
                    </dd>
                  </div>
                  <div>
                    <dt>Case type</dt>
                    <dd>
                      <Value value={primary.caseType} />
                    </dd>
                  </div>
                  <div>
                    <dt>Rule name</dt>
                    <dd>
                      <Value value={primary.ruleName} />
                    </dd>
                  </div>
                  <div>
                    <dt>Case created in source</dt>
                    <dd>
                      <Value value={primary.sourceCreatedAt} />
                    </dd>
                  </div>
                  <div>
                    <dt>Archived in source</dt>
                    <dd>
                      <Value value={primary.reportedArchived} />
                    </dd>
                  </div>
                </dl>
              )}
              {typeof linkedCustomerId === 'number' && (
                <button
                  className="neo-link"
                  onClick={() =>
                    open({ kind: 'customers', id: linkedCustomerId })
                  }
                >
                  Open customer history
                </button>
              )}
              {primary?.metadataAvailable === false && (
                <p>
                  Case metadata and customer link unavailable. Screening
                  observations are preserved.
                </p>
              )}
              {primary?.profileAvailable === false && (
                <p>
                  Customer profile unavailable. Related historical records are
                  preserved.
                </p>
              )}
              <nav
                className="neo-cm1-tabs"
                aria-label="Archive record sections"
              >
                {sections.map((s) => (
                  <button
                    key={s}
                    aria-pressed={section === s}
                    onClick={() => {
                      setSection(s);
                      setPage(1);
                      setDateFrom('');
                      setDateTo('');
                    }}
                  >
                    {label(s)}
                  </button>
                ))}
              </nav>
            </>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setQuery(text.trim());
                setPage(1);
              }}
            >
              <label>
                Search in
                <select
                  value={kind}
                  onChange={(e) => {
                    setKind(e.target.value);
                    setPage(1);
                    setVersion('');
                    setFlag('');
                    setState('');
                    setDateFrom('');
                    setDateTo('');
                  }}
                >
                  {kinds.map((k) => (
                    <option key={k} value={k}>
                      {k === 'issues'
                        ? 'Import issues'
                        : k === 'hits'
                        ? 'Screening hits'
                        : label(k)}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Search value
                <input
                  value={text}
                  maxLength={255}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={
                    kind === 'timeline'
                      ? 'Comment, analyst, case ID or source action'
                      : 'CM1 ID, name or search text'
                  }
                />
              </label>
              <button type="submit">Search archive</button>
            </form>
          )}
          {!target && kind === 'customers' && (
            <p className="neo-field-help">
              Use an exact customer ID, identification number or email, or the
              beginning of a name or surname.
            </p>
          )}
          {!target && kind === 'cases' && (
            <label>
              Reported case state
              <select
                value={state}
                onChange={(e) => {
                  setState(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All</option>
                <option value="archived">Archived</option>
                <option value="active">Active</option>
                <option value="unknown">Unavailable</option>
              </select>
            </label>
          )}
          {(currentKind === 'hits' || (!target && kind === 'cases')) && (
            <div className="neo-filters">
              <label>
                Screening export
                <select
                  value={version}
                  onChange={(e) => {
                    setVersion(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">Default export</option>
                  {summary.data.hitVersions.map((v) => (
                    <option key={v.datasetVersion} value={v.datasetVersion}>
                      {v.datasetVersion}
                      {v.isDefault ? ' (default)' : ''}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Hit category
                <select
                  value={flag}
                  onChange={(e) => {
                    setFlag(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">All</option>
                  <option value="pep">PEP</option>
                  <option value="rca">RCA</option>
                  <option value="sanction">Sanction</option>
                </select>
              </label>
            </div>
          )}
          {!!dateLabel[currentKind] && (
            <div className="neo-filters">
              <label>
                {dateLabel[currentKind]} from
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => {
                    setDateFrom(e.target.value);
                    setPage(1);
                  }}
                />
              </label>
              <label>
                Through
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => {
                    setDateTo(e.target.value);
                    setPage(1);
                  }}
                />
              </label>
            </div>
          )}
          {target && section === 'parameters' && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setParameterFilter(parameterText.trim());
                setPage(1);
              }}
            >
              <label>
                Parameter name or value
                <input
                  maxLength={255}
                  value={parameterText}
                  onChange={(e) => setParameterText(e.target.value)}
                />
              </label>
              <button type="submit">Filter parameters</button>
            </form>
          )}
          {currentKind === 'timeline' && (
            <p className="neo-field-help">
              Case handling events and their comments, oldest first. Dates and
              analyst names are shown as recorded in CM1. A case&apos;s archived
              status does not mean every event is a closure.
            </p>
          )}
          {target && section === 'notes' && (
            <p className="neo-field-help">
              Comments recorded during case handling are in Case history &amp;
              comments.
            </p>
          )}
          {active.isFetching && (
            <p role="status">Loading historical records…</p>
          )}
          {active.isError && (
            <p role="alert">
              {active.error instanceof Error
                ? active.error.message
                : 'Unable to load archive records.'}
            </p>
          )}
          {active.data && (
            <>
              <p>
                {active.data.total.toLocaleString()}{' '}
                {active.data.total === 1 ? 'record' : 'records'}
              </p>
              {!active.data.records.length && (
                <p>
                  {currentKind === 'risk'
                    ? 'No populated risk observations were supplied.'
                    : 'No records found in this section.'}
                </p>
              )}
              {active.data.records.map((row) => (
                <RecordCard
                  key={row.id}
                  row={row}
                  kind={currentKind}
                  run={run || String(summary.data.run)}
                  open={open}
                />
              ))}
              <div className="neo-pager">
                <button
                  disabled={page === 1 || active.isFetching}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Previous
                </button>
                <span>Page {page}</span>
                <button
                  disabled={
                    page * active.data.size >= active.data.total ||
                    active.isFetching
                  }
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
}
